<template>
  <div class="action-menu">
    <div
      ref="slider"
      :class="['action-menu__slider']"
    />
    <div class="action-menu__menu" id="actionMenu">
      <action-menu-tab
        ref="backgroundTab"
        text="Background"
        :is-active="applicationStore.activeActionTab === ActionMenuTabs.BACKGROUND"
        @toggle-active="onToggleActive(ActionMenuTabs.BACKGROUND)"
      >
        <background-icon/>
      </action-menu-tab>
      <action-menu-tab
        ref="textTab"
        text="Text"
        :is-active="applicationStore.activeActionTab === ActionMenuTabs.TEXT"
        @toggle-active="onToggleActive(ActionMenuTabs.TEXT)"
      >
        <text-icon/>
      </action-menu-tab>
      <action-menu-tab
        ref="logoTab"
        text="Logo + Stickers"
        :is-active="applicationStore.activeActionTab === ActionMenuTabs.STICKERS"
        @toggle-active="onToggleActive(ActionMenuTabs.STICKERS)"
      >
        <logo-icon/>
      </action-menu-tab>
      <action-menu-tab
        ref="effectsTab"
        text="Effects"
        :is-active="applicationStore.activeActionTab === ActionMenuTabs.EFFECTS"
        @toggle-active="onToggleActive(ActionMenuTabs.EFFECTS)"
      >
        <effects-icon/>
      </action-menu-tab>
    </div>
  </div>
</template>

<script setup lang="ts">

import { ref, ComponentPublicInstance, watch } from 'vue'

import BackgroundIcon from '@/assets/BackgroundIcon.svg'
import EffectsIcon from '@/assets/EffectsIcon.svg'
import LogoIcon from '@/assets/LogoIcon.svg'
import TextIcon from '@/assets/TextIcon.svg'
import ActionMenuTab from '@/components/action-menu/ActionMenuTab.vue'
import { useApplicationStore, ActionMenuTabs } from '@/store/application-store'
import { useProjectStore } from '@/store/project-store'
import useTextStates from '@/composables/UseTextStates'

const tabPadding = '32px'
const sliderPos = ref(tabPadding)

const backgroundTab = ref<ComponentPublicInstance | null>(null)
const textTab = ref<ComponentPublicInstance | null>(null)
const logoTab = ref<ComponentPublicInstance | null>(null)
const effectsTab = ref<ComponentPublicInstance | null>(null)
const slider = ref<HTMLDivElement | null>(null)

const projectStore = useProjectStore()
const applicationStore = useApplicationStore()

const setSliderPosition = (tab: ActionMenuTabs) => {
  let activeElement

  switch (tab) {
  case ActionMenuTabs.BACKGROUND:
    if (backgroundTab.value) {
      activeElement = backgroundTab.value.$el as HTMLElement
    }
    break
  case ActionMenuTabs.TEXT:
    if (textTab.value) {
      activeElement = textTab.value.$el as HTMLElement
    }
    break
  case ActionMenuTabs.STICKERS:
    if (logoTab.value) {
      activeElement = logoTab.value.$el as HTMLElement
    }
    break
  case ActionMenuTabs.EFFECTS:
    if (effectsTab.value) {
      activeElement = effectsTab.value.$el as HTMLElement
    }
    break
  }
  if (activeElement && slider.value) {
    sliderPos.value = (activeElement.offsetWidth / 2) + activeElement.offsetLeft - (slider.value.offsetWidth / 2) + 'px' // set slider center to match the selected element's center
  }
}
const { addText } = useTextStates()

const onToggleActive = (tab: ActionMenuTabs) => {
  setSliderPosition(tab)
  applicationStore.activeActionTab = tab

  if (tab === ActionMenuTabs.TEXT) {
    if (projectStore.textStates.length === 0) {
      addText({ style: applicationStore.selectedText?.style })
    }
  } else {
    applicationStore.selectedText = null
  }

  if (tab !== ActionMenuTabs.STICKERS) {
    applicationStore.selectedSticker = null
  }
}

// Add application activeActionTab listener when editing text
watch(() => applicationStore.activeActionTab, () => {
  onToggleActive(applicationStore.activeActionTab)
})

</script>

<style lang="scss">

.action-menu {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 88px;
  background-color: $black;
  border-radius: 0px 0px 8px 8px;
  padding: 0px v-bind(tabPadding);

  &__slider {
    width: 70px;
    height: 4px;
    background-color: $purple-light;
    border-radius: 0px 0px 2px 2px;
    position: absolute;
    top: 0px;
    left: v-bind(sliderPos);
    transition: all 260ms $animationCurve;
  }

  &__menu {
    display: flex;
    justify-content: space-between;
  }
}

</style>
